/**
 * The walkthrough, in the app.
 *
 * It renders `docs/demo/building-a-product.md` rather than restating it, so
 * there is one copy of the prose. Two versions of the same explanation drift,
 * and the one nobody is looking at is always the one that goes stale.
 *
 * The markdown is our own file, shipped with the build — not user input — so
 * rendering it to HTML is safe here in a way it would not be for anything a
 * person typed.
 */
import { useEffect, useState } from "react";
import { marked } from "marked";
import { Loading, PageHead } from "../components/Page.tsx";

const SOURCE = "/guide/building-a-product.md";

export function Guide() {
  const [html, setHtml] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let current = true;
    fetch(SOURCE)
      .then((response) => {
        if (!response.ok) throw new Error("not built");
        return response.text();
      })
      .then(async (markdown) => {
        if (!current) return;
        // The document's own title becomes the page heading, so it is not
        // shown twice; links to the repo's own files are dropped.
        const body = markdown.replace(/^#\s.*\n/, "");
        const rendered = await marked.parse(body, { gfm: true, breaks: false });
        setHtml(rewriteAssetPaths(rendered));
      })
      .catch(() => current && setMissing(true));
    return () => {
      current = false;
    };
  }, []);

  return (
    <div className="stack">
      <PageHead
        title="Building a product"
        sub="A walkthrough for anyone opening Dynamic Portal for the first time — about two minutes to watch, ten to read."
      />

      {missing ? (
        <div className="card">
          <div className="empty">
            <h3>The walkthrough has not been generated yet</h3>
            <p className="small">Run <span className="key">npm run demo</span> to build it from the running application.</p>
          </div>
        </div>
      ) : html === null ? (
        <Loading what="the walkthrough" />
      ) : (
        <>
          <div className="card">
            <video className="guide-video" controls preload="metadata" poster="/guide/frames/03.png">
              <source src="/guide/building-a-product.mp4" type="video/mp4" />
              Your browser cannot play this video. The written walkthrough below covers the same ground.
            </video>
          </div>
          <article className="card prose" dangerouslySetInnerHTML={{ __html: html }} />
        </>
      )}
    </div>
  );
}

/**
 * The markdown is written to be read in the repository, where images sit
 * beside it. Served from /guide, those relative paths need the prefix — and
 * the link to the video file itself becomes redundant, since it is playing
 * directly above.
 */
function rewriteAssetPaths(html: string): string {
  return html
    .replace(/src="frames\//g, 'src="/guide/frames/')
    .replace(/href="building-a-product\.mp4"/g, 'href="/guide/building-a-product.mp4"');
}
